import { createSlice } from '@reduxjs/toolkit';
import type {
  FeatureToggles,
  WhitelabelSlug,
  IMerchantResponse,
  IUiVisibility,
  IPaymentMethod,
} from '@shore/types';
import {
  defaultUiVisibility,
  getUiVisibility,
} from '../utils/default-ui-visibility';
import { initializeSuccess, updateTrialPeriod } from '../actions';

interface InitializeSuccessPayload {
  currentMerchantData: IMerchantResponse<{
    withFeatureToggles: true;
    withOrg: true;
  }>;
  packageCode: string | null;
  sessionRole: string | null;
  subscription?: {
    status?: string;
    customer?: { partner?: string };
    trial_end?: string | null;
    trial_days_left?: number | null;
  };
  paymentSettings?: {
    payment_methods?: IPaymentMethod[];
  };
}

export interface CurrentMerchantState {
  id: string | null;
  name: string;
  whiteLabel: WhitelabelSlug | null;
  createdAt: string | null;
  isSelfSignUp: boolean;
  subscriptionStatus: string | undefined;
  subscriptionPartner: string | undefined;
  trialEndsOn: string | null;
  trialDaysLeft: number | null;
  featureToggles: FeatureToggles;
  source: string;
  uiVisibility: IUiVisibility;
  category: string;
  currency: string;
  isTest: boolean;
  packageCode: string | null;
  pixelId: string | null;
  phoneNumber: string | null;
  sessionRole: string | null;
  paymentSettings: {
    paymentMethods: IPaymentMethod[];
  } | null;
}

const initialState: CurrentMerchantState = {
  id: null,
  name: '',
  whiteLabel: null,
  createdAt: null,
  isSelfSignUp: false,
  subscriptionStatus: undefined,
  subscriptionPartner: undefined,
  trialEndsOn: null,
  trialDaysLeft: null,
  featureToggles: {},
  source: '',
  uiVisibility: defaultUiVisibility,
  category: '',
  currency: 'EUR',
  isTest: false,
  packageCode: null,
  pixelId: null,
  phoneNumber: null,
  sessionRole: null,
  paymentSettings: null,
};

const merchantSlice = createSlice({
  name: 'currentMerchant',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeSuccess, (state, action) => {
        const {
          currentMerchantData,
          packageCode,
          sessionRole,
          subscription,
          paymentSettings,
        } = action.payload as InitializeSuccessPayload;

        const org = currentMerchantData.organization;

        return {
          ...state,
          id: currentMerchantData.id,
          name: currentMerchantData.name,
          createdAt: currentMerchantData.created_at,
          whiteLabel: org?.whitelabel_slug ?? null,
          isSelfSignUp: org?.self_sign_up ?? false,
          subscriptionStatus: subscription?.status || undefined,
          subscriptionPartner: subscription?.customer?.partner || undefined,
          trialEndsOn: subscription?.trial_end ?? org?.trial_ends_on ?? null,
          trialDaysLeft: subscription
            ? subscription.trial_days_left ?? null
            : org?.trial_days_left ?? null,
          isTest: org?.test ?? false,
          packageCode: packageCode ?? org?.package_code ?? null,
          featureToggles: currentMerchantData.feature_toggles ?? {},
          source: currentMerchantData.source,
          category: currentMerchantData.category,
          currency: currentMerchantData.currency,
          uiVisibility: getUiVisibility(currentMerchantData),
          pixelId: currentMerchantData.pixel_id,
          phoneNumber: currentMerchantData.phone || '',
          sessionRole,
          paymentSettings: {
            paymentMethods: paymentSettings?.payment_methods || [],
          },
        };
      })
      .addCase(updateTrialPeriod, (state, action) => {
        state.trialEndsOn = action.payload as string;
      });
  },
});

export const currentMerchantReducer = merchantSlice.reducer;
